import { FC, ReactNode } from 'react';
import {
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import './BasicDialog.scss';
import clsx from 'clsx';
import { Close } from '@mui/icons-material';

interface BasicDialogProps {
  visible: boolean;
  title: string;
  description?: string;
  children?: ReactNode;
  actions?: ReactNode;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  ctaString?: string;
  cancelString?: string;
  hideCancel?: boolean;
  showCloseIcon?: boolean;
  width?: Breakpoint;
  isLoading?: boolean;
  disabled?: boolean;
  onYes: () => void;
  onClose: () => void;
  className?: string;
}

const BasicDialog: FC<BasicDialogProps> = ({
  visible,
  title,
  description,
  children,
  actions,
  onKeyDown,
  ctaString = 'Done',
  cancelString = 'Cancel',
  hideCancel = false,
  showCloseIcon = false,
  width = 'sm',
  isLoading = false,
  disabled = false,
  onYes,
  onClose,
  className,
}) => {
  const isDeleteBtn = !actions && (ctaString === 'Discard' || ctaString === 'Delete');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);

    if (event.defaultPrevented) {
      return;
    }

    if (isDeleteBtn && !isLoading && event.key === 'Enter') {
      event.preventDefault();
      onYes();
    }
  };

  return (
    <Dialog
      open={visible}
      onClose={onClose}
      maxWidth={width}
      fullWidth
      data-testid="basic-dialog"
      className={clsx('basic-dialog', className)}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle data-testid="basic-dialog-title" className="basic-dialog__title">
        <Typography variant="h3" className="basic-dialog__title-text">
          {title}
        </Typography>
        {showCloseIcon && (
          <IconButton
            className="basic-dialog__close"
            aria-label="Close dialog"
            onClick={onClose}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent className="basic-dialog__content">
        {children ? (
          children
        ) : description ? (
          <Typography variant="body2" className="basic-dialog__content-text">
            {description}
          </Typography>
        ) : null}
      </DialogContent>
      <div className="basic-dialog__divider-container">
        <Divider className="basic-dialog__divider" />
      </div>
      <DialogActions className="basic-dialog__actions">
        {actions ? (
          actions
        ) : (
          <>
            {!hideCancel && (
              <Button onClick={onClose}>
                <Stack gap={1} direction="row" alignItems="center">
                  <Typography variant="body2" className="basic-dialog__actions__cta-text">
                    {cancelString}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="basic-dialog__actions__shortcut-indicator"
                  >
                    Esc
                  </Typography>
                </Stack>
              </Button>
            )}
            <LoadingButton
              onClick={onYes}
              loading={isLoading}
              disabled={isLoading || disabled}
              data-testid="basic-dialog-cta-yes"
              className="basic-dialog__actions__cta-button"
            >
              <Stack gap={1} direction="row" alignItems="center">
                <Typography
                  variant="body2"
                  className="basic-dialog__actions__cta-text basic-dialog__actions__cta-text--bold-white"
                >
                  {ctaString}
                </Typography>
                {isDeleteBtn && (
                  <Typography
                    variant="subtitle2"
                    className="basic-dialog__actions__shortcut-indicator basic-dialog__actions__shortcut-indicator--white"
                  >
                    Enter
                  </Typography>
                )}
              </Stack>
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BasicDialog;
